/**
 * @fileoverview Upload step of the WIP spreadsheet import (timesheet settings).
 *
 * Uploads the firm's WIP export to Convex storage, creates an import run and
 * kicks off analysis. Nothing is written to the firm's configuration here —
 * the review screen does that after the user confirms.
 *
 * Mirrors WorkflowImportDialog's upload flow (upload URL → POST → storageId).
 */

import { useRef, useState } from "react";
import { useConvex } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { FileSpreadsheet, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/ui/primitives";
import { Spinner } from "@/ui/primitives/spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { extractErrorMessage } from "@/utils/errors";

const ACCEPT = ".csv,.xlsx,.xls";

export function WipImportDialog({
  open,
  onOpenChange,
  orgId,
  onAnalysed,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: Id<"organizations">;
  onAnalysed: (runId: Id<"timesheetImportRuns">) => void;
}) {
  const convex = useConvex();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const handleImport = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const url = await convex.mutation(
        api.timesheets.import.runs.generateUploadUrl,
        { orgId },
      );
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });
      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }
      const { storageId } = (await response.json()) as {
        storageId: Id<"_storage">;
      };

      const runId = await convex.mutation(
        api.timesheets.import.runs.createRun,
        { orgId, storageId, fileName: file.name },
      );
      await convex.action(
        api.timesheets.import.analyseAction.analyseWipImport,
        {
          runId,
        },
      );

      setFile(null);
      onAnalysed(runId);
      onOpenChange(false);
    } catch (e) {
      toast.error(extractErrorMessage(e, "We couldn't read that spreadsheet."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="size-4 text-primary" />
            Import from spreadsheet
          </DialogTitle>
          <DialogDescription>
            Upload an exported timesheet or WIP and Morgan will analyse billing
            increments, time categories, staff grades and rates. Nothing is
            saved until you review it.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="flex w-full items-center gap-3 rounded-lg border border-dashed border-border px-4 py-5 text-left transition-colors hover:bg-muted/50 disabled:opacity-60"
          >
            {file ? (
              <FileSpreadsheet className="size-6 shrink-0 text-primary" />
            ) : (
              <Upload className="size-6 shrink-0 text-muted-foreground" />
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-primary">
                {file ? file.name : "Choose a file"}
              </p>
              <p className="text-xs text-muted-foreground">
                {file
                  ? "Click to choose a different file"
                  : "One matter's WIP export (.csv, .xlsx, .xls)"}
              </p>
            </div>
          </button>
        </div>

        {busy ? (
          <p className="text-sm text-muted-foreground">
            Reading the spreadsheet and matching it to your settings…
          </p>
        ) : null}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button onClick={() => void handleImport()} disabled={!file || busy}>
            {busy ? (
              <>
                <Spinner className="mr-1.5" />
                Analysing…
              </>
            ) : (
              <>
                <Sparkles className="mr-1.5 size-4" />
                Analyse spreadsheet
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
